'use client';

import { useState, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useI18n } from '@/lib/i18n/context';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const STORAGE_KEY = 'pwa-gallery-views';
const DISMISS_KEY = 'pwa-banner-dismissed';
const TRIGGER_EVERY = 3; // hiện sau mỗi 3 lần xem gallery

export function PWAInstaller() {
  const [visible, setVisible] = useState(false);
  const [closed, setClosed] = useState(false);
  const [closeable, setCloseable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIOS, setIsIOS] = useState(false);

  const pathname = usePathname();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reopenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { t } = useI18n();

  // Check installed + capture install prompt
  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    const ios = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
    setIsIOS(ios);

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Đếm lần xem gallery theo pathname
  useEffect(() => {
    if (isInstalled) return;

    // Chỉ đếm các trang gallery (tuỳ chỉnh pattern cho đúng route của bạn)
    const isGalleryPage = pathname.startsWith('/p/') || pathname.startsWith('/post/') || pathname.startsWith('/media/');
    if (!isGalleryPage) return;

    const count = parseInt(sessionStorage.getItem(STORAGE_KEY) || '0', 10) + 1;
    sessionStorage.setItem(STORAGE_KEY, String(count));

    // Bỏ qua lần đầu tiên, hiện sau mỗi bội số của TRIGGER_EVERY
    // if (count === 1 || count % TRIGGER_EVERY !== 0) return;

    // Nếu user đã dismiss trong phiên này, bỏ qua
    if (sessionStorage.getItem(DISMISS_KEY)) return;

    setClosed(false);
    setVisible(false);
    setCloseable(false);

    timerRef.current = setTimeout(() => {
      setVisible(true);
      closeTimerRef.current = setTimeout(() => {
        setCloseable(true);
      }, 3000);
    }, 1000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, [pathname, isInstalled]);

  // Sau khi đóng, 120s hiện lại (nếu chưa cài)
  useEffect(() => {
    if (!closed) return;

    reopenTimerRef.current = setTimeout(() => {
      if (sessionStorage.getItem(DISMISS_KEY)) return;
      setClosed(false);
      setVisible(false);
      setCloseable(false);

      timerRef.current = setTimeout(() => {
        setVisible(true);
        closeTimerRef.current = setTimeout(() => setCloseable(true), 3000);
      }, 500);
    }, 120_000);

    return () => {
      if (reopenTimerRef.current) clearTimeout(reopenTimerRef.current);
    };
  }, [closed]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
      sessionStorage.setItem(DISMISS_KEY, '1');
    }
    setDeferredPrompt(null);
    setVisible(false);
    setClosed(true);
  };

  const handleClose = () => {
    if (!closeable) return;
    setClosed(true);
    setVisible(false);
    // Không hiện lại trong session sau khi user chủ động đóng
    sessionStorage.setItem(DISMISS_KEY, '1');
  };

  const appName = process.env.NEXT_PUBLIC_APP_NAME || 'MyApp';

  console.log({ isInstalled, deferredPrompt, isIOS, visible, closed });

  if (isInstalled || closed) return null;
  if (!visible) return null;

//   if (isInstalled || closed || (!deferredPrompt && !isIOS)) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '1.5rem',
        right: '1.5rem',
        zIndex: 9999,
        width: 'min(320px, calc(100vw - 2rem))',
        transition: 'opacity 0.6s ease, transform 0.6s ease',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(40px)',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      <div style={{ position: 'relative' }}>
        {/* Nút đóng */}
        <button
          onClick={handleClose}
          aria-label="Đóng"
          style={{
            position: 'absolute',
            top: '-10px',
            right: '-10px',
            zIndex: 10000,
            width: '22px',
            height: '22px',
            borderRadius: '50%',
            background: 'rgba(0,0,0,0.75)',
            color: '#fff',
            border: 'none',
            cursor: closeable ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '11px',
            lineHeight: 1,
            transition: 'opacity 0.3s ease',
            opacity: closeable ? 1 : 0,
            pointerEvents: closeable ? 'auto' : 'none',
            padding: 0,
          }}
        >
          ✕
        </button>

        {/* Banner body */}
        <div
          style={{
            background: '#111111',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '14px',
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          {/* Icon */}
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'rgba(255,255,255,0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="rgba(255,255,255,0.7)" strokeWidth="1.8"
              strokeLinecap="round" strokeLinejoin="round">
              <rect x="5" y="2" width="14" height="20" rx="2" />
              <line x1="12" y1="18" x2="12.01" y2="18" />
            </svg>
          </div>

          {/* Text */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              margin: 0,
              fontSize: '13px',
              fontWeight: 500,
              color: '#ffffff',
              lineHeight: 1.3,
            }}>
              {t.pwa.title || `Cài ${appName} để xem nhanh hơn!`}
            </p>
            <p style={{
              margin: '2px 0 0',
              fontSize: '11px',
              color: 'rgba(255,255,255,0.5)',
              lineHeight: 1.3,
            }}>
              {isIOS
                ? t.pwa.iosSubtitle || 'Nhấn Share → Add to Home Screen'
                : t.pwa.androidSubtitle || 'Nhận thông báo bộ ảnh mới ngay khi ra mắt'}
            </p>
          </div>

          {/* CTA */}
          {!isIOS && (
            <button
              onClick={handleInstall}
              style={{
                flexShrink: 0,
                background: '#ffffff',
                color: '#000000',
                border: 'none',
                borderRadius: '8px',
                padding: '7px 13px',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {t.pwa.installButton || 'Cài đặt'}
            </button>
          )}
        </div>

        <p style={{
          textAlign: 'center',
          fontSize: '10px',
          color: 'rgba(255,255,255,0.25)',
          margin: '4px 0 0',
        }}>
          {t.pwa.disclaimer || 'Cài đặt nhanh, không cần app store.'}
        </p>
      </div>
    </div>
  );
}